import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import {
  Alert,
  Box,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Typography
} from '@mui/material'
import React, {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { TLStoreSnapshot } from 'tldraw'
import 'tldraw/tldraw.css'

import ErrorBoundary from '../ErrorBoundary/ErrorBoundary'

// Lazy load the Tldraw component
const Tldraw = lazy(() =>
  import('tldraw').then(module => ({ default: module.Tldraw }))
)

export interface OfficeDrawingViewerProps {
  drawingData: string
  officeName: string
}

const DrawingViewerSkeleton: React.FC = () => (
  <Box sx={{ width: '100%', minHeight: '300px' }}>
    <Skeleton variant='rectangular' width='100%' height={300} />
  </Box>
)

const OfficeDrawingViewerContent: React.FC<OfficeDrawingViewerProps> = ({
  drawingData,
  officeName
}) => {
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const editorRef = useRef<any>(null)

  // Parse drawing data
  const snapshot = useMemo(() => {
    if (!drawingData) {
      setLoadError('No drawing data available')
      return undefined
    }
    try {
      return JSON.parse(drawingData) as TLStoreSnapshot
    } catch (error) {
      console.error('Failed to parse drawing data:', error)
      setLoadError('Failed to load drawing. The data may be corrupted.')
      return undefined
    }
  }, [drawingData])

  // Handle editor mount to make it read-only and zoom to fit
  const handleMount = (editor: any) => {
    editorRef.current = editor

    // Make truly read-only - disable all interactions
    editor.updateInstanceState({
      isReadonly: true
    })

    // Disable selection and interaction
    editor.setCurrentTool('hand')

    // Zoom to fit all content in the viewport
    setTimeout(() => {
      editor.zoomToFit({ duration: 0 })
    }, 100)
  }

  // Handle fullscreen toggle
  const handleFullscreenToggle = () => {
    if (!containerRef.current) return

    if (!isFullscreen) {
      // Enter fullscreen
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen()
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen()
      }
    }
  }

  // Listen for fullscreen changes and re-zoom to fit
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement
      setIsFullscreen(isNowFullscreen)

      // Re-zoom to fit when entering/exiting fullscreen with delay for viewport resize
      if (editorRef.current) {
        setTimeout(() => {
          editorRef.current.zoomToFit({ duration: 0 })
        }, 500)
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  if (loadError) {
    return (
      <Card className='office-drawing-viewer'>
        <CardContent>
          <Alert severity='error'>{loadError}</Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className='office-drawing-viewer' ref={containerRef}>
      <CardContent>
        {!isFullscreen && (
          <Typography variant='h6' component='h2' gutterBottom>
            Office Layout
          </Typography>
        )}

        <Box
          aria-label={`Office layout drawing for ${officeName}`}
          sx={{
            height: isFullscreen ? '100vh' : '300px',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            position: 'relative',
            '& .tlui-layout__top': { display: 'none' },
            '& .tlui-help-menu': { display: 'none' },
            '& .tlui-navigation-zone': { display: 'none' },
            '& .tlui-debug-panel': { display: 'none' },
            '& .tlui-style-panel': { display: 'none' },
            '& .tlui-toolbar': { display: 'none' },
            '& .tlui-button': { pointerEvents: 'none' },
            '& .tl-container': {
              width: '100% !important',
              height: '100% !important',
              maxWidth: '100%',
              maxHeight: '100%',
              pointerEvents: 'none'
            },
            '& .tl-canvas': {
              width: '100% !important',
              height: '100% !important',
              pointerEvents: 'none'
            },
            '& .tl-overlays': {
              pointerEvents: 'none'
            }
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1000
            }}
          >
            <IconButton
              onClick={handleFullscreenToggle}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              color='primary'
              sx={{
                bgcolor: 'background.paper',
                '&:hover': { bgcolor: 'background.paper' }
              }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Box>
          <div
            style={{
              height: '100%',
              width: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              pointerEvents: 'none'
            }}
          >
            <Tldraw
              autoFocus={false}
              snapshot={snapshot}
              hideUi={true}
              onMount={handleMount}
              components={{
                PageMenu: null,
                MainMenu: null,
                QuickActions: null,
                ActionsMenu: null,
                HelpMenu: null,
                NavigationPanel: null,
                SharePanel: null,
                Toolbar: null
              }}
            />
          </div>
        </Box>
      </CardContent>
    </Card>
  )
}

const OfficeDrawingViewer: React.FC<OfficeDrawingViewerProps> = props => {
  const [error, setError] = useState<Error | null>(null)

  const handleError = (error: Error) => {
    console.error('Drawing viewer error:', error)
    setError(error)
  }

  if (error) {
    return (
      <Card className='office-drawing-viewer'>
        <CardContent>
          <Alert severity='error'>
            Failed to load office layout drawing. Please refresh the page and
            try again.
          </Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <ErrorBoundary
      onError={handleError}
      errorComponent={
        <Card className='office-drawing-viewer'>
          <CardContent>
            <Alert severity='error'>
              Failed to load office layout drawing. Please refresh the page and
              try again.
            </Alert>
          </CardContent>
        </Card>
      }
    >
      <Suspense fallback={<DrawingViewerSkeleton />}>
        <OfficeDrawingViewerContent {...props} />
      </Suspense>
    </ErrorBoundary>
  )
}

export default OfficeDrawingViewer
